import { useState } from "react";
import { Languages, Check, X, ChevronDown, ChevronUp } from "lucide-react";

// Available languages for presenter selection
const AVAILABLE_LANGUAGES = [
  { code: "pt", name: "Português", nativeName: "Português" },
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Español", nativeName: "Español" },
  { code: "fr", name: "Français", nativeName: "Français" },
  { code: "de", name: "Deutsch", nativeName: "Deutsch" },
  { code: "it", name: "Italiano", nativeName: "Italiano" },
  { code: "ru", name: "Russkiy", nativeName: "Русский" },
  { code: "ja", name: "Nihongo", nativeName: "日本語" },
  { code: "ko", name: "Hangugeo", nativeName: "한국어" },
  { code: "zh", name: "Zhongwen", nativeName: "中文" },
  { code: "ar", name: "Al-Arabiyyah", nativeName: "العربية" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी" },
  { code: "fil", name: "Filipino", nativeName: "Filipino" },
  { code: "nl", name: "Nederlands", nativeName: "Nederlands" },
  { code: "pl", name: "Polski", nativeName: "Polski" },
  { code: "tr", name: "Türkçe", nativeName: "Türkçe" },
  { code: "vi", name: "Tiếng Việt", nativeName: "Tiếng Việt" },
  { code: "th", name: "Thai", nativeName: "ไทย" },
  { code: "sv", name: "Svenska", nativeName: "Svenska" },
  { code: "uk", name: "Ukrainska", nativeName: "Українська" },
];

export function PresenterLanguagesSelector({ 
  selectedLanguages = [], 
  onChange,
  label = "Idiomas dos Apresentadores",
  description = "Selecione todos os idiomas que serão falados neste evento"
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleLanguage = (code) => {
    if (selectedLanguages.includes(code)) {
      // Don't allow removing if it's the only one
      if (selectedLanguages.length > 1) {
        onChange(selectedLanguages.filter(l => l !== code));
      }
    } else {
      onChange([...selectedLanguages, code]);
    }
  };

  const getLanguageName = (code) => {
    const lang = AVAILABLE_LANGUAGES.find(l => l.code === code);
    return lang ? lang.nativeName : code.toUpperCase();
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Languages className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-white">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {selectedLanguages.length} selecionado{selectedLanguages.length !== 1 ? 's' : ''}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Selected languages pills */}
      {selectedLanguages.length > 0 && !isExpanded && (
        <div className="flex flex-wrap gap-1">
          {selectedLanguages.map(code => (
            <span 
              key={code}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full"
            >
              {getLanguageName(code)}
            </span>
          ))}
        </div>
      )}

      {/* Expanded language grid */}
      {isExpanded && (
        <div className="p-3 bg-zinc-900/50 border border-zinc-700 rounded-lg space-y-3">
          <p className="text-xs text-gray-500">{description}</p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {AVAILABLE_LANGUAGES.map((lang) => {
              const isSelected = selectedLanguages.includes(lang.code);
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => toggleLanguage(lang.code)}
                  className={`flex items-center justify-between p-2 rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                      : 'bg-zinc-800/50 border-zinc-700 text-gray-400 hover:border-zinc-600 hover:text-gray-300'
                  }`}
                >
                  <span className="text-xs truncate">{lang.nativeName}</span>
                  {isSelected && <Check className="w-3 h-3 flex-shrink-0 ml-1" />}
                </button>
              );
            })}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { AVAILABLE_LANGUAGES };
